import React, { useEffect, useState } from 'react';
import { Accordion, Box, ContentList, ContentListTitle, InputLabel } from '@hopskipdrive/react-components';
import { VariancesPopover } from '@rideiq/shared/components';
import { PageHeading } from '@rideiq/shared/components/PageHeading';

import { getCalendarDetails } from '../../../api/services/Calendars';
import { formatDate } from '../../../utils/helpers';
import { RideCalendarBreakDetails } from '../RideCalendarBreakDetails';

import {
  CalendarDetails,
  DateSectionContainer,
  DateText,
  MiniOption,
  MiniOptionBox,
  SeriesDateStringBox,
} from './DateSection.styles';

interface DateSectionAccordionProps {
  rideDetailsDefault: RideDetails;
  days: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };
}

export const DateSectionAccordion: React.FC<DateSectionAccordionProps> = ({ rideDetailsDefault, days }) => {
  const [expanded, setExpanded] = useState(false);
  const [calendarDetails, setCalendarDetails] = useState<CalendarDetail>();

  function seriesDateString() {
    let startDate;
    let endDate;

    if (rideDetailsDefault?.tripTemplate?.startDate && rideDetailsDefault?.tripTemplate?.endDate) {
      const dateFormat = 'M/dd/yy';

      startDate = formatDate(rideDetailsDefault.tripTemplate.startDate, dateFormat);
      endDate = formatDate(rideDetailsDefault.tripTemplate.endDate, dateFormat);
    }

    return startDate && endDate ? `${startDate} - ${endDate}` : '';
  }

  function displayVarianceCount() {
    if (rideDetailsDefault?.tripTemplate?.scheduleVariances?.length) {
      return (
        <Box
          className="variance-count"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <VariancesPopover variances={rideDetailsDefault.tripTemplate.scheduleVariances} />
        </Box>
      );
    }

    return null;
  }

  useEffect(() => {
    const calendarId = rideDetailsDefault?.calendarSlug;

    async function loadCalendarDetails() {
      if (calendarId) {
        const response = await getCalendarDetails(calendarId);

        if (response?.type === 'success') {
          setCalendarDetails(response.data.calendar);
        }
      }
    }

    calendarId && loadCalendarDetails();
  }, []);

  return (
    <DateSectionContainer mb={4} id="content-panel">
      <Accordion expanded={expanded} data-testid="date-section-accordion">
        <Accordion.Header
          style={{ userSelect: 'text' }}
          onClick={() => (!isTextHighlighted() ? setExpanded(!expanded) : null)}
        >
          <Box>
            <PageHeading variant="h2" style={{ marginRight: '21px', marginTop: '3px' }}>
              Ride Series:
            </PageHeading>
          </Box>
          <MiniOptionBox>
            {days.monday && <MiniOption mr={1} label="M" active={days.monday} />}
            {days.tuesday && <MiniOption mr={1} label="T" active={days.tuesday} />}
            {days.wednesday && <MiniOption mr={1} label="W" active={days.wednesday} />}
            {days.thursday && <MiniOption mr={1} label="Th" active={days.thursday} />}
            {days.friday && <MiniOption mr={1} label="F" active={days.friday} />}
            {days.saturday && <MiniOption mr={1} label="Sa" active={days.saturday} />}
            {days.sunday && <MiniOption mr={1} label="Su" active={days.sunday} />}
          </MiniOptionBox>
          <SeriesDateStringBox>
            <DateText>{seriesDateString()}</DateText>
          </SeriesDateStringBox>
          {displayVarianceCount()}
        </Accordion.Header>
        <Accordion.Body>
          <ContentList>
            <ContentListTitle>
              <InputLabel>Calendar</InputLabel>
            </ContentListTitle>
            <CalendarDetails>
              {calendarDetails && <RideCalendarBreakDetails calendar={calendarDetails} />}
            </CalendarDetails>
          </ContentList>
        </Accordion.Body>
      </Accordion>
    </DateSectionContainer>
  );
};

const isTextHighlighted = () => window.getSelection()?.toString();
